import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { type MockSalesOrder } from '@/mocks/billing';
import { salesOrderService } from '@/services/salesOrderService';
import { formatINR } from '@/utils/format';

const today = new Date().toISOString().split('T')[0];

function daysBetween(a: string, b: string) {
  return Math.floor((new Date(a).getTime() - new Date(b).getTime()) / 86400000);
}

export default function SalesOrderReportPage() {
  const navigate = useNavigate();
  const [salesOrders, setSalesOrders] = useState<MockSalesOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Fetch sales orders dynamically from database API
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await salesOrderService.listSalesOrders();
      setSalesOrders(data || []);
    } catch (err) {
      console.warn('Failed to load sales orders for Order Book:', err);
      setSalesOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const openOrders = useMemo(() => {
    return salesOrders.filter((o) => o.status === 'CONFIRMED' || o.status === 'PARTIAL');
  }, [salesOrders]);

  // ─── Section 1: Summary cards ───
  const totalOpenValue = useMemo(() => {
    return openOrders.reduce((s, o) => s + (o.pendingAmount || 0), 0);
  }, [openOrders]);

  const ordersOverdue = useMemo(() => {
    return openOrders.filter((o) => o.expectedDeliveryDate && o.expectedDeliveryDate < today).length;
  }, [openOrders]);

  const avgFulfillment = useMemo(() => {
    const partials = salesOrders.filter((o) => o.status === 'PARTIAL');
    if (partials.length === 0) return 0;
    return Math.round(
      partials.reduce((s, o) => {
        const items = o.items || [];
        const totalQ = items.reduce((a, i) => a + i.orderedQty, 0);
        const delQ = items.reduce((a, i) => a + i.deliveredQty, 0);
        return s + (totalQ > 0 ? (delQ / totalQ) * 100 : 0);
      }, 0) / partials.length
    );
  }, [salesOrders]);

  const completedThisMonth = useMemo(() => {
    return salesOrders.filter((o) => {
      if (o.status !== 'COMPLETED') return false;
      const d = new Date(o.date);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  }, [salesOrders]);

  // ─── Section 2: Pending Delivery Table (flat, per item) ───
  const pendingRows = useMemo(() => {
    const rows: Array<{
      soNumber: string;
      soId: string;
      soDate: string;
      customerName: string;
      customerId: string;
      warehouseId?: string;
      itemName: string;
      itemId: string;
      itemCode?: string;
      orderedQty: number;
      deliveredQty: number;
      pendingQty: number;
      unit: string;
      unitId?: string;
      rate: number;
      taxRate?: number;
      expectedDeliveryDate: string | null;
      soStatus: string;
    }> = [];

    openOrders.forEach((so) => {
      (so.items || []).forEach((item) => {
        const pending = item.pendingQty ?? (item.orderedQty - item.deliveredQty);
        if (pending > 0) {
          rows.push({
            soNumber: so.soNumber,
            soId: so.id,
            soDate: so.date,
            customerName: so.customerName,
            customerId: so.customerId,
            warehouseId: (so as any).warehouseId,
            itemName: item.itemName,
            itemId: item.itemId,
            itemCode: item.itemCode,
            orderedQty: item.orderedQty,
            deliveredQty: item.deliveredQty,
            pendingQty: pending,
            unit: item.unit || 'Pcs',
            unitId: item.unitId,
            rate: item.rate,
            taxRate: item.taxRate,
            expectedDeliveryDate: so.expectedDeliveryDate || null,
            soStatus: so.status,
          });
        }
      });
    });

    let filtered = rows;
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.soNumber.toLowerCase().includes(q) ||
          r.customerName.toLowerCase().includes(q) ||
          r.itemName.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'All') {
      filtered = filtered.filter((r) => r.soStatus === statusFilter.toUpperCase());
    }
    if (dateFrom) filtered = filtered.filter((r) => r.soDate >= dateFrom);
    if (dateTo) filtered = filtered.filter((r) => r.soDate <= dateTo);

    return filtered;
  }, [openOrders, search, statusFilter, dateFrom, dateTo]);

  // ─── Section 3: Customer-wise Pending ───
  const customerWise = useMemo(() => {
    const map = new Map<
      string,
      { customerName: string; customerId: string; totalOrders: number; openValue: number; oldestDate: string }
    >();
    openOrders.forEach((so) => {
      const key = so.customerId || so.customerName;
      const existing = map.get(key);
      if (existing) {
        existing.totalOrders += 1;
        existing.openValue += so.pendingAmount || 0;
        if (so.date < existing.oldestDate) existing.oldestDate = so.date;
      } else {
        map.set(key, {
          customerName: so.customerName,
          customerId: so.customerId,
          totalOrders: 1,
          openValue: so.pendingAmount || 0,
          oldestDate: so.date,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => b.openValue - a.openValue);
  }, [openOrders]);

  const handleCreateChallan = (row: (typeof pendingRows)[0]) => {
    navigate('/sales/challans', {
      state: {
        fromSalesOrder: {
          soId: row.soId,
          soNumber: row.soNumber,
          customerId: row.customerId,
          customerName: row.customerName,
          warehouseId: row.warehouseId,
          items: [
            {
              itemId: row.itemId,
              itemCode: row.itemCode,
              itemName: row.itemName,
              orderedQty: row.orderedQty,
              deliveredQty: row.deliveredQty,
              pendingQty: row.pendingQty,
              qty: row.pendingQty,
              unit: row.unit,
              unitId: row.unitId,
              rate: row.rate,
              taxRate: row.taxRate,
            },
          ],
        },
      },
    });
  };

  const handleExportCSV = () => {
    const headers = [
      'SO Number',
      'Customer',
      'Item',
      'Ordered',
      'Delivered',
      'Pending',
      'Unit',
      'Rate',
      'Pending Value',
      'Expected Date',
      'Overdue Days',
    ];
    const csvRows = [headers.join(',')];
    pendingRows.forEach((r) => {
      const overdueDays =
        r.expectedDeliveryDate && r.expectedDeliveryDate < today ? daysBetween(today, r.expectedDeliveryDate) : 0;
      csvRows.push(
        [
          r.soNumber,
          `"${r.customerName}"`,
          `"${r.itemName}"`,
          r.orderedQty,
          r.deliveredQty,
          r.pendingQty,
          r.unit,
          r.rate,
          r.pendingQty * r.rate,
          r.expectedDeliveryDate || '',
          overdueDays,
        ].join(',')
      );
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `OrderBook_${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fl =
    'w-full h-9 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 text-slate-700';

  const getRowStyle = (expectedDate: string | null) => {
    if (!expectedDate) return '';
    const diff = daysBetween(expectedDate, today);
    if (diff < 0) return 'bg-red-50/60';
    if (diff <= 7) return 'bg-amber-50/60';
    return '';
  };

  return (
    <AppLayout>
      <div className="p-6 bg-[#f8fafc] min-h-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Order Book</h1>
            <p className="text-sm text-slate-500">Open and pending order tracking</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/sales/orders')}
              className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-arrow-left-line" /> Back to Orders
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-download-line" /> Export CSV
            </button>
          </div>
        </div>

        {/* Section 1: Order Book Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Total Open Value</p>
            <p className="text-2xl font-bold text-indigo-600">{formatINR(totalOpenValue)}</p>
            <p className="text-xs text-slate-400 mt-0.5">{openOrders.length} open orders</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Orders Overdue</p>
            <p className="text-2xl font-bold text-red-600">{ordersOverdue}</p>
            <p className="text-xs text-slate-400 mt-0.5">Past expected delivery</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Avg Fulfillment %</p>
            <p className="text-2xl font-bold text-amber-600">{avgFulfillment}%</p>
            <p className="text-xs text-slate-400 mt-0.5">Across partial orders</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Completed This Month</p>
            <p className="text-2xl font-bold text-emerald-600">{completedThisMonth}</p>
            <p className="text-xs text-slate-400 mt-0.5">Fully delivered</p>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search SO, customer, or item..."
                className={fl}
              />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${fl} w-36`}>
              <option value="All">All Status</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Partial">Partial</option>
            </select>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className={`${fl} w-40`}
            />
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={`${fl} w-40`} />
            <button
              onClick={() => {
                setSearch('');
                setStatusFilter('All');
                setDateFrom('');
                setDateTo('');
              }}
              className="h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium text-slate-500 hover:bg-slate-50 cursor-pointer whitespace-nowrap transition-colors"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Section 2: Pending Delivery Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-6">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Pending Delivery Items</h2>
            {loading && <span className="text-xs text-slate-400">Loading DB data...</span>}
          </div>
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading Order Book from database...</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {['SO Number', 'Customer', 'Item', 'Ordered', 'Delivered', 'Pending', 'Expected Date', 'Overdue?', 'Action'].map(
                    (h) => (
                      <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 whitespace-nowrap">
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {pendingRows.map((row) => {
                  const overdueDays =
                    row.expectedDeliveryDate && row.expectedDeliveryDate < today
                      ? daysBetween(today, row.expectedDeliveryDate)
                      : 0;
                  const rowBg = getRowStyle(row.expectedDeliveryDate);
                  return (
                    <tr
                      key={`${row.soId}-${row.itemId}`}
                      className={`border-b border-slate-50 hover:bg-indigo-50/30 transition-colors ${rowBg}`}
                    >
                      <td
                        className="px-4 py-3 font-medium text-indigo-600 cursor-pointer"
                        onClick={() => navigate(`/sales/orders/${row.soId}`)}
                      >
                        {row.soNumber}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-800">{row.customerName}</td>
                      <td className="px-4 py-3 text-slate-600">{row.itemName}</td>
                      <td className="px-4 py-3 text-center text-slate-700">{row.orderedQty}</td>
                      <td className="px-4 py-3 text-center text-emerald-600 font-medium">{row.deliveredQty}</td>
                      <td className="px-4 py-3 text-center font-semibold text-amber-600">{row.pendingQty}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-600">{row.expectedDeliveryDate || '—'}</td>
                      <td className="px-4 py-3">
                        {overdueDays > 0 ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 font-medium whitespace-nowrap">
                            {overdueDays} days overdue
                          </span>
                        ) : row.expectedDeliveryDate ? (
                          (() => {
                            const d = daysBetween(row.expectedDeliveryDate, today);
                            return d <= 7 ? (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 font-medium whitespace-nowrap">
                                Due soon
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">On track</span>
                            );
                          })()
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {/* Create Challan commented out
                        <button
                          onClick={() => handleCreateChallan(row)}
                          className="text-xs px-2.5 py-1.5 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 cursor-pointer whitespace-nowrap font-medium transition-colors"
                        >
                          Create Challan
                        </button>
                        */}
                      </td>
                    </tr>
                  );
                })}
                {pendingRows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-slate-400">
                      No pending items found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Section 3: Customer-wise Pending */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Customer-Wise Pending</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {['Customer', 'Total Orders', 'Open Value', 'Oldest Overdue'].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {customerWise.map((c) => {
                const oldestOverdue = openOrders
                  .filter(
                    (o) =>
                      (o.customerId === c.customerId || o.customerName === c.customerName) &&
                      o.expectedDeliveryDate &&
                      o.expectedDeliveryDate < today
                  )
                  .sort((a, b) => a.expectedDeliveryDate!.localeCompare(b.expectedDeliveryDate!))[0];
                return (
                  <tr key={c.customerName} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-800">{c.customerName}</td>
                    <td className="px-4 py-3 text-slate-600">{c.totalOrders}</td>
                    <td className="px-4 py-3 font-semibold text-indigo-600">{formatINR(c.openValue)}</td>
                    <td className="px-4 py-3">
                      {oldestOverdue ? (
                        <span className="text-red-600 font-medium">
                          {oldestOverdue.expectedDeliveryDate} ({daysBetween(today, oldestOverdue.expectedDeliveryDate!)} days)
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {customerWise.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-slate-400">
                    No pending orders by customer
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  );
}